import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "../App";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import axios from "axios";
import { 
  Plus, Search, Beaker, Sparkles, Trash2, Edit2, 
  Wind, AlertTriangle, ArrowUpDown, AlertCircle, X, Upload, CheckCircle, Download,
  ChevronDown, RefreshCw
} from "lucide-react";

export default function InventoryPage() {
  const { token } = useAuth();
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [noteTypeFilter, setNoteTypeFilter] = useState(""); // Changed from durationFilter
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showCSVImportDialog, setShowCSVImportDialog] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [deletingMaterial, setDeletingMaterial] = useState(null); // Add delete confirmation state
  const [aiOptimizing, setAiOptimizing] = useState(false); // Add AI optimization state

  const categories = ["Synthetic", "Natural", "Essential Oil", "Absolute", "Isolate", "Base", "Accord"];
  const noteTypes = ["top", "heart", "base"]; // Changed from durations

  const fetchMaterials = async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append("search", searchQuery);
      if (categoryFilter) params.append("category", categoryFilter);
      if (noteTypeFilter) params.append("note_type", noteTypeFilter); // Changed parameter name
      params.append("sort", sortBy);
      params.append("sort_order", sortOrder);

      const response = await axios.get(`${API}/materials?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMaterials(response.data);
    } catch (error) {
      toast.error("Failed to fetch materials");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMaterials();
  }, [searchQuery, categoryFilter, noteTypeFilter, sortBy, sortOrder, token]);

  const handleDelete = async () => {
    if (!deletingMaterial) return;
    
    try {
      await axios.delete(`${API}/materials/${deletingMaterial.material_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Material deleted");
      fetchMaterials();
    } catch (error) {
      toast.error("Failed to delete material");
    } finally {
      setDeletingMaterial(null);
    }
  };

  const optimizeInventory = async (batchSize = 5, onlyIncomplete = true, resumeFrom = null) => {
    if (materials.length === 0) {
      toast.error("No materials to optimize");
      return;
    }

    setAiOptimizing(true);
    try {
      const params = { 
        batch_size: batchSize, 
        only_incomplete: onlyIncomplete 
      };
      
      // Add resume functionality for large inventories
      if (resumeFrom) {
        params.resume_from = resumeFrom;
      }

      const response = await axios.post(
        `${API}/ai/optimize-inventory`,
        {},
        {
          params: params,
          headers: { Authorization: `Bearer ${token}` },
          timeout: 90000  // 90 second timeout for optimization requests
        }
      );
      
      const result = response.data;
      
      if (result.optimized > 0) {
        let message = `✅ Batch Complete! Enhanced ${result.optimized} materials`;
        
        // Show processing mode for transparency
        if (result.processing_mode) {
          message += ` [${result.processing_mode}${result.fast_mode ? ' - AI Only' : ''}]`;
        }
        
        // Show progress for large inventories
        if (result.total_inventory > 50) {
          message += ` (${result.progress_percent}% complete)`;
        }
        
        if (result.has_more) {
          message += ` - ${result.remaining_count} materials remaining`;
        }
        
        toast.success(message, { duration: 6000 });
        
        // Show processing mode explanation for fast mode
        if (result.fast_mode) {
          toast.info("⚡ Fast Mode Active: Using AI-only processing for large inventory (no external lookups for faster performance)", {
            duration: 8000
          });
        }
        
        // Show suggestion if provided
        if (result.suggestion) {
          toast.info(`💡 Tip: ${result.suggestion}`, {
            duration: 6000
          });
        }
        
        // Show detailed results
        if (result.details && result.details.length > 0) {
          console.log("Optimization Details:", result.details);
          
          const optimizedMaterials = result.details.filter(d => d.status === "optimized");
          if (optimizedMaterials.length > 0) {
            const summary = optimizedMaterials.slice(0, 3).map(d => 
              `${d.material}: Added ${d.fields_added.join(", ")}`
            ).join("\n");
            
            toast.info(`Sample optimizations:\n${summary}${optimizedMaterials.length > 3 ? `\n...and ${optimizedMaterials.length - 3} more` : ""}`, {
              duration: 6000
            });
          }
        }
        
        // Refresh the materials list to show new data
        fetchMaterials();
        
        // Auto-continue for large inventories with remaining materials
        if (result.has_more && result.total_inventory > 100 && result.remaining_count > 0) {
          const continueOptimization = () => {
            toast.info(`🔄 Auto-continuing optimization: ${result.remaining_count} materials remaining...`, {
              duration: 4000
            });
            
            // Continue with next batch after a short delay
            setTimeout(() => {
              optimizeInventory(batchSize, onlyIncomplete, result.resume_from);
            }, 3000); // 3 second delay between batches
          };
          
          // For very large inventories, auto-continue
          if (result.total_inventory > 200) {
            continueOptimization();
          } else {
            // For medium inventories, ask user
            setTimeout(() => {
              toast.info(
                `Continue optimization? ${result.remaining_count} materials remaining. Click 'Optimize with AI' to continue.`, 
                { duration: 12000 }
              );
            }, 2000);
          }
        }
      } else {
        let message = `📋 Analysis complete. `;
        if (result.only_incomplete) {
          message += `No materials found that need optimization.`;
        } else {
          message += `${result.skipped} materials already have all AI information.`;
        }
        
        // Show progress even when no optimization needed
        if (result.total_inventory > 50) {
          message += ` (${result.progress_percent}% of inventory checked)`;
        }
        
        toast.info(message);
      }
      
      if (result.errors > 0) {
        toast.warning(`⚠️ ${result.errors} materials had processing errors.`);
      }
      
    } catch (error) {
      console.error("AI Optimization Error:", error);
      
      // Handle specific timeout errors (504, 540)
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        toast.error("⏱️ Request timeout. Try with smaller batch size or check your internet connection.", {
          duration: 8000
        });
      } else if (error.response?.status === 504 || error.response?.status === 540) {
        toast.error("⏱️ Gateway timeout (504/540). The server is processing but taking too long. Try refreshing and using 'Optimize with AI' again for smaller batches.", {
          duration: 10000
        });
      } else {
        let errorMessage = "Failed to optimize inventory";
        if (error.response?.data?.detail) {
          if (Array.isArray(error.response.data.detail)) {
            errorMessage = error.response.data.detail.map(err => err.msg || err).join("; ");
          } else if (typeof error.response.data.detail === 'string') {
            errorMessage = error.response.data.detail;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        toast.error("Failed to optimize inventory: " + errorMessage);
      }
    } finally {
      setAiOptimizing(false);
    }
  };

  const optimizeIndividualMaterial = async (material) => {
    setAiOptimizing(true);
    try {
      const response = await axios.post(
        `${API}/ai/optimize-material/${material.material_id}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 25000  // 25s timeout to match backend
        }
      );
      
      const result = response.data;
      
      if (result.optimized) {
        let message = `✨ ${result.material} optimized! Enhanced: ${result.fields_optimized.join(", ")}`;
        if (result.processing_mode) {
          message += ` [${result.processing_mode}]`;
        }
        
        toast.success(message, {
          duration: 6000
        });
        
        // ALWAYS refresh materials to show updated data - this was missing before
        await fetchMaterials();
        
      } else {
        if (result.error === "AI timeout with no fallback") {
          // Even on timeout, try refreshing in case partial data was saved
          toast.warning(`⏱️ ${result.material} optimization timed out, but checking for partial updates...`, {
            duration: 4000
          });
          
          // Refresh to check if any data was actually saved despite timeout message
          setTimeout(async () => {
            await fetchMaterials();
          }, 1000);
          
        } else if (result.suggestion) {
          toast.info(`📋 ${result.material}: ${result.message}`, {
            duration: 6000
          });
        } else {
          toast.info(`📋 ${result.material} ${result.message.includes('already has all') ? 'already optimized' : 'could not be optimized'}`, {
            duration: 4000
          });
        }
      }
    } catch (error) {
      console.error("Individual material optimization error:", error);
      
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        // Even on frontend timeout, refresh to check if backend completed the work
        toast.error(`⏱️ Request timeout for ${material.name}. Checking if optimization completed anyway...`, {
          duration: 6000
        });
        
        // Refresh after timeout to check if backend actually completed the work
        setTimeout(async () => {
          await fetchMaterials();
          toast.info(`Refreshed data for ${material.name}. If information appeared, the optimization actually worked despite the timeout.`, {
            duration: 6000
          });
        }, 2000);
        
      } else {
        let errorMessage = `Failed to optimize ${material.name}`;
        if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail;
        }
        toast.error(errorMessage, { duration: 6000 });
      }
    } finally {
      setAiOptimizing(false);
    }
  };

  // Force re-optimize ALL materials in inventory, overwriting existing AI-generated values
  const reoptimizeInventory = async (fields = null) => {
    if (materials.length === 0) {
      toast.error("No materials to re-optimize");
      return;
    }
    
    // Start immediately without confirmation dialog
    setAiOptimizing(true);
    let successCount = 0;
    let errorCount = 0;
    
    const fieldsText = fields ? fields.join(", ") : "all fields";
    toast.info(`🔄 Starting re-optimization of ${materials.length} materials (${fieldsText})...`, { duration: 4000 });
    
    for (let i = 0; i < materials.length; i++) {
      const material = materials[i];
      try {
        const response = await axios.post(
          `${API}/ai/reoptimize-material/${material.material_id}`,
          { fields: fields },
          {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 35000
          }
        );
        
        if (response.data.optimized) {
          successCount++;
        }
        
        // Progress update every 5 materials
        if ((i + 1) % 5 === 0) {
          toast.info(`Progress: ${i + 1}/${materials.length} materials processed`, { duration: 2000 });
        }
        
        // Small delay to prevent overwhelming the server
        await new Promise(resolve => setTimeout(resolve, 500));
        
      } catch (error) {
        console.error(`Failed to re-optimize ${material.name}:`, error);
        errorCount++;
      }
    }
    
    setAiOptimizing(false);
    await fetchMaterials();
    
    toast.success(`✅ Re-optimization complete! ${successCount} updated, ${errorCount} errors`, {
      duration: 6000
    });
  };

  const bulkDeleteInventory = async () => {
    if (materials.length === 0) {
      toast.error("No materials to delete");
      return;
    }

    // Show confirmation dialog
    const confirmed = window.confirm(
      `⚠️ Are you sure you want to delete ALL ${materials.length} materials?\n\nThis action cannot be undone and will permanently remove all materials from your inventory.`
    );

    if (!confirmed) return;

    try {
      const response = await axios.delete(`${API}/materials/bulk-delete`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Successfully deleted ${response.data.deleted_count} materials`);
      fetchMaterials(); // Refresh the list
    } catch (error) {
      console.error("Bulk delete error:", error);
      let errorMessage = "Failed to delete inventory";
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(err => err.msg || err).join("; ");
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error("Failed to delete inventory: " + errorMessage);
    }
  };

  // Export inventory as CSV
  const exportCSV = () => {
    if (materials.length === 0) {
      toast.error("No materials to export");
      return;
    }

    // Helper to sanitize text for CSV (remove newlines, escape quotes)
    const sanitizeForCSV = (text) => {
      if (!text) return '';
      // Convert to string, replace newlines with spaces, escape double quotes
      return String(text)
        .replace(/\r\n/g, ' ')
        .replace(/\n/g, ' ')
        .replace(/\r/g, ' ')
        .replace(/"/g, '""')
        .trim();
    };

    // Helper to extract simple IFRA limit (just the percentage or "No restriction")
    const extractSimpleIFRA = (ifraText) => {
      if (!ifraText) return '';
      const text = String(ifraText);
      // If it's a simple value like "100.0" or "No restriction", return as-is
      if (/^[\d.]+$/.test(text.trim()) || text.toLowerCase().includes('no restriction')) {
        return text.trim().substring(0, 50); // Limit length
      }
      // If it's a long AI-generated response, try to extract a percentage
      const percentMatch = text.match(/(\d+\.?\d*)\s*%/);
      if (percentMatch) {
        return percentMatch[1];
      }
      // Otherwise return a truncated clean version
      return text.substring(0, 50).replace(/\n/g, ' ').trim();
    };

    // Define CSV headers (matching database field names for compatibility)
    const headers = [
      "name",
      "category",
      "quantity_grams",
      "price_per_gram",
      "solution_percentage",
      "solution_type",
      "supplier",
      "cas_number",
      "note_type",
      "scent_duration",
      "odor_impact",
      "ifra_limit",
      "odor_profile",
      "description",
      "synonyms"
    ];

    // Convert materials to CSV rows
    const rows = materials.map(m => [
      `"${sanitizeForCSV(m.name)}"`,
      `"${sanitizeForCSV(m.category)}"`,
      m.quantity_grams || 0,
      m.price_per_gram || 0,
      m.solution_percentage || 100,
      `"${sanitizeForCSV(m.solution_type || 'Alcohol')}"`,
      `"${sanitizeForCSV(m.supplier)}"`,
      `"${sanitizeForCSV(m.cas_number)}"`,
      `"${sanitizeForCSV(m.note_type)}"`,
      `"${sanitizeForCSV(m.scent_duration)}"`,
      m.odor_impact || 0,
      `"${extractSimpleIFRA(m.ifra_limit)}"`,
      `"${sanitizeForCSV(m.odor_profile)}"`,
      `"${sanitizeForCSV(m.description)}"`,
      `"${sanitizeForCSV(m.synonyms)}"`
    ]);

    // Build CSV content
    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.join(","))
    ].join("\n");

    // Download file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `scentic_inventory_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success(`Exported ${materials.length} materials to CSV`);
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  // Calculate usage percentage
  const getUsagePercent = (material) => {
    const initial = material.initial_quantity_grams || material.quantity_grams || 0;
    const current = material.quantity_grams || 0;
    if (initial <= 0) return 0;
    return ((initial - current) / initial) * 100;
  };

  return (
    <div className="min-h-screen bg-noir flex">
      <Sidebar />
      
      <main className="flex-1 p-8 ml-64">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="font-playfair text-3xl font-bold text-white mb-2">
                Material Inventory
              </h1>
              <p className="text-zinc-400">{materials.length} materials</p>
            </div>
            <div className="flex gap-3">
              <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                <DialogTrigger asChild>
                  <Button 
                    data-testid="add-material-dialog-btn"
                    className="bg-amber hover:bg-amber-light text-black font-semibold uppercase tracking-widest text-xs"
                  >
                    <Plus className="w-4 h-4 mr-2" /> Add Material
                  </Button>
                </DialogTrigger>
                <MaterialDialog 
                  material={null}
                  onClose={() => setShowAddDialog(false)}
                  onSuccess={() => {
                    setShowAddDialog(false);
                    fetchMaterials();
                  }}
                  token={token}
                />
              </Dialog>
              <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
                <DialogTrigger asChild>
                  <Button 
                    className="bg-green-600 hover:bg-green-500 text-white font-semibold uppercase tracking-widest text-xs mr-3"
                  >
                    <Upload className="w-4 h-4 mr-2" /> Import Receipt
                  </Button>
                </DialogTrigger>
                <ImportReceiptDialog 
                  onClose={() => setShowImportDialog(false)}
                  onSuccess={() => {
                    setShowImportDialog(false);
                    fetchMaterials();
                  }}
                  token={token}
                />
              </Dialog>
              <Dialog open={showCSVImportDialog} onOpenChange={setShowCSVImportDialog}>
                <DialogTrigger asChild>
                  <Button 
                    className="bg-teal-600 hover:bg-teal-500 text-white font-semibold uppercase tracking-widest text-xs mr-3"
                    title="Import materials from a CSV file"
                  >
                    <Upload className="w-4 h-4 mr-2" /> Import CSV
                  </Button>
                </DialogTrigger>
                <ImportCSVDialog 
                  onClose={() => setShowCSVImportDialog(false)}
                  onSuccess={() => {
                    setShowCSVImportDialog(false);
                    fetchMaterials();
                  }}
                  token={token}
                />
              </Dialog>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    disabled={aiOptimizing || materials.length === 0}
                    className="bg-blue-600 hover:bg-blue-500 text-white font-semibold uppercase tracking-widest text-xs mr-3"
                    title="AI Optimization Options"
                    data-testid="optimize-inventory-dropdown"
                  >
                    {aiOptimizing ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Optimizing...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4" />
                        Optimize with AI
                        <ChevronDown className="w-3 h-3 ml-1" />
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64 bg-noir-paper border-zinc-700">
                  <DropdownMenuLabel className="text-zinc-400 text-xs">Optimize Missing Data</DropdownMenuLabel>
                  <DropdownMenuItem 
                    onSelect={() => optimizeInventory(1, true)}
                    className="text-zinc-200 hover:bg-zinc-800 cursor-pointer"
                  >
                    <Sparkles className="w-4 h-4 mr-2 text-blue-400" />
                    Optimize Incomplete Materials
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-zinc-700" />
                  <DropdownMenuLabel className="text-zinc-400 text-xs">Re-Optimize (Overwrite Existing)</DropdownMenuLabel>
                  <DropdownMenuItem 
                    onSelect={() => reoptimizeInventory(null)}
                    className="text-zinc-200 hover:bg-zinc-800 cursor-pointer"
                  >
                    <RefreshCw className="w-4 h-4 mr-2 text-amber-400" />
                    Re-Optimize All Fields
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onSelect={() => reoptimizeInventory(['odor_impact'])}
                    className="text-zinc-300 hover:bg-zinc-800 cursor-pointer text-sm"
                  >
                    Odor Impact Only
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onSelect={() => reoptimizeInventory(['scent_duration'])}
                    className="text-zinc-300 hover:bg-zinc-800 cursor-pointer text-sm"
                  >
                    Duration Only
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onSelect={() => reoptimizeInventory(['synonyms'])}
                    className="text-zinc-300 hover:bg-zinc-800 cursor-pointer text-sm"
                  >
                    Synonyms Only
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onSelect={() => reoptimizeInventory(['ifra_limit'])}
                    className="text-zinc-300 hover:bg-zinc-800 cursor-pointer text-sm"
                  >
                    IFRA Limits Only
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                onClick={exportCSV}
                disabled={materials.length === 0}
                className="bg-purple-600 hover:bg-purple-500 text-white font-semibold uppercase tracking-widest text-xs mr-3"
                title="Export inventory to CSV file"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              <Button
                onClick={bulkDeleteInventory}
                disabled={materials.length === 0}
                variant="destructive"
                className="bg-red-600 hover:bg-red-500 text-white font-semibold uppercase tracking-widest text-xs"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete All
              </Button>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-4">
            <div className="flex-1 min-w-[200px] max-w-md relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                data-testid="material-search-input"
                placeholder="Search materials, CAS numbers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-noir-paper border-zinc-800 focus:border-amber text-white"
              />
            </div>
            <Select value={categoryFilter || "all"} onValueChange={(v) => setCategoryFilter(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="category-filter" className="w-[150px] bg-noir-paper border-zinc-800 text-white">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={noteTypeFilter || "all"} onValueChange={(v) => setNoteTypeFilter(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="note-type-filter" className="w-[140px] bg-noir-paper border-zinc-800 text-white">
                <SelectValue placeholder="Note" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Notes</SelectItem>
                {noteTypes.map(note => (
                  <SelectItem key={note} value={note}>{note.charAt(0).toUpperCase() + note.slice(1)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Materials List View */}
          {loading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-2 border-amber border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : materials.length === 0 ? (
            <Card className="bg-noir-paper border-zinc-800">
              <CardContent className="py-12 text-center">
                <Beaker className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <h3 className="text-white font-medium mb-2">No materials found</h3>
                <p className="text-zinc-500 mb-4">
                  {searchQuery || categoryFilter || noteTypeFilter 
                    ? "Try adjusting your filters"
                    : "Start by adding your first material"
                  }
                </p>
                <Button 
                  onClick={() => setShowAddDialog(true)}
                  className="bg-amber hover:bg-amber-light text-black"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add Material
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="bg-noir-paper border border-zinc-800 overflow-hidden">
              {/* Table Header */}
              <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-noir-subtle border-b border-zinc-800 text-xs uppercase tracking-wider text-zinc-500">
                <button 
                  onClick={() => toggleSort("name")}
                  className="col-span-3 flex items-center gap-1 hover:text-white text-left"
                >
                  Name
                  <ArrowUpDown className="w-3 h-3" />
                </button>
                <button 
                  onClick={() => toggleSort("note_type")}
                  className="col-span-1 flex items-center justify-center gap-1 hover:text-white"
                >
                  Note
                  <ArrowUpDown className="w-3 h-3" />
                </button>
                <button 
                  onClick={() => toggleSort("odor_impact")}
                  className="col-span-1 flex items-center justify-center gap-1 hover:text-white"
                >
                  Impact
                  <ArrowUpDown className="w-3 h-3" />
                </button>
                <button 
                  onClick={() => toggleSort("scent_duration")}
                  className="col-span-1 flex items-center justify-center gap-1 hover:text-white"
                >
                  Duration
                  <ArrowUpDown className="w-3 h-3" />
                </button>
                <div className="col-span-2 text-center">Stock</div>
                <div className="col-span-2 text-center">Reserve (100%)</div>
                <div className="col-span-1">CAS Number(s)</div>
                <div className="col-span-1 text-right">Actions</div>
              </div>

              {/* Table Body */}
              <div className="divide-y divide-zinc-800/50">
                {materials.map((material) => {
                  const usagePercent = getUsagePercent(material);
                  const isLowStock = usagePercent >= 80;
                  const hasStock = (material.quantity_grams || 0) > 0;
                  const hasReserve = (material.reserve_grams || 0) > 0;
                  
                  // Get all CAS numbers
                  const casNumbers = material.cas_numbers?.length > 0 
                    ? material.cas_numbers 
                    : (material.cas_number ? [material.cas_number] : []);

                  return (
                    <div 
                      key={material.material_id}
                      className="grid grid-cols-12 gap-2 px-4 py-3 hover:bg-zinc-800/30 items-center"
                    >
                      {/* Name, Synonyms & Category */}
                      <div className="col-span-3">
                        <div className="flex items-center gap-2">
                          {isLowStock && hasStock && (
                            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                          )}
                          <div className="min-w-0">
                            <p className="text-white font-medium truncate">{material.name}</p>
                            {material.synonyms && (
                              <p className="text-blue-400 text-xs truncate" title={material.synonyms}>
                                {material.synonyms}
                              </p>
                            )}
                            {material.category && (
                              <p className="text-zinc-500 text-xs">{material.category}</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Note Type */}
                      <div className="col-span-1 text-center">
                        {material.note_type ? (
                          <span className={`inline-flex px-2 py-0.5 text-xs ${
                            material.note_type === "top" ? "bg-blue-500/20 text-blue-400" :
                            material.note_type === "heart" ? "bg-purple-500/20 text-purple-400" :
                            "bg-amber/20 text-amber"
                          }`}>
                            {material.note_type}
                          </span>
                        ) : (
                          <span className="text-zinc-600">—</span>
                        )}
                      </div>

                      {/* Odor Impact */}
                      <div className="col-span-1 text-center">
                        <span className={`font-mono text-sm ${
                          material.odor_impact <= 100 ? "text-zinc-400" :
                          material.odor_impact <= 400 ? "text-amber" :
                          "text-red-400"
                        }`}>
                          {material.odor_impact || "—"}
                        </span>
                      </div>

                      {/* Scent Duration */}
                      <div className="col-span-1 text-center">
                        {material.scent_duration ? (
                          <span className="text-green-400 text-xs font-mono">
                            {material.scent_duration}
                          </span>
                        ) : (
                          <span className="text-zinc-600">—</span>
                        )}
                      </div>

                      {/* Stock (Blue) */}
                      <div className="col-span-2 text-center">
                        {hasStock ? (
                          <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-blue-500/10 border border-blue-500/30">
                            <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                            <span className="text-blue-400 font-mono text-sm">{material.quantity_grams}g</span>
                            {isLowStock && (
                              <AlertCircle className="w-3 h-3 text-red-500" />
                            )}
                          </div>
                        ) : (
                          <span className="text-zinc-600 text-xs">No stock</span>
                        )}
                      </div>

                      {/* Reserve (Green) */}
                      <div className="col-span-2 text-center">
                        {hasReserve ? (
                          <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-green-500/10 border border-green-500/30">
                            <div className="w-2 h-2 rounded-full bg-green-500"></div>
                            <span className="text-green-400 font-mono text-sm">{material.reserve_grams}g</span>
                          </div>
                        ) : (
                          <span className="text-zinc-600 text-xs">—</span>
                        )}
                      </div>

                      {/* CAS Numbers */}
                      <div className="col-span-1">
                        {material.components?.length > 0 ? (
                          <div className="space-y-0.5">
                            <p className="text-purple-400 text-xs font-medium">{material.components.length} comp</p>
                            {material.components.slice(0, 1).map((comp, idx) => (
                              <p key={idx} className="text-zinc-400 text-xs font-mono truncate" title={`${comp.name}: ${comp.ratio}%`}>
                                {comp.cas_number || comp.name}
                              </p>
                            ))}
                            {material.components.length > 1 && (
                              <p className="text-zinc-500 text-xs">+{material.components.length - 1}</p>
                            )}
                          </div>
                        ) : casNumbers.length > 0 ? (
                          <div className="space-y-0.5">
                            {casNumbers.slice(0, 1).map((cas, idx) => (
                              <p key={idx} className="text-zinc-400 text-xs font-mono truncate">{cas}</p>
                            ))}
                            {casNumbers.length > 1 && (
                              <p className="text-zinc-500 text-xs">+{casNumbers.length - 1}</p>
                            )}
                          </div>
                        ) : (
                          <span className="text-zinc-600 text-xs">—</span>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="col-span-1 flex justify-end gap-1">
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          onClick={() => optimizeIndividualMaterial(material)}
                          disabled={aiOptimizing}
                          className="h-8 w-8 text-zinc-400 hover:text-blue-400"
                          title="Optimize this material with AI"
                        >
                          <Sparkles className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          onClick={() => setEditingMaterial(material)}
                          className="h-8 w-8 text-zinc-400 hover:text-white"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          onClick={() => setDeletingMaterial(material)}
                          className="h-8 w-8 text-zinc-400 hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Edit Dialog */}
          {editingMaterial && (
            <Dialog open={!!editingMaterial} onOpenChange={() => setEditingMaterial(null)}>
              <MaterialDialog 
                material={editingMaterial}
                onClose={() => setEditingMaterial(null)}
                onSuccess={() => {
                  setEditingMaterial(null);
                  fetchMaterials();
                }}
                token={token}
              />
            </Dialog>
          )}

          {/* Delete Confirmation Dialog */}
          {deletingMaterial && (
            <Dialog open={!!deletingMaterial} onOpenChange={() => setDeletingMaterial(null)}>
              <DialogContent className="bg-noir-paper border-zinc-800">
                <DialogHeader>
                  <DialogTitle className="text-white font-playfair">
                    Delete Material
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p className="text-zinc-400">
                    Are you sure you want to delete <span className="text-white font-medium">"{deletingMaterial.name}"</span>?
                  </p>
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded">
                    <p className="text-red-400 text-sm">
                      ⚠️ This action cannot be undone. The material will be permanently removed from your inventory.
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="ghost" className="text-zinc-400">Cancel</Button>
                  </DialogClose>
                  <Button
                    onClick={handleDelete}
                    className="bg-red-600 hover:bg-red-500 text-white"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Material
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </main>
    </div>
  );
}

// Import Receipt Dialog Component
const ImportReceiptDialog = ({ onClose, onSuccess, token }) => {
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [parsedItems, setParsedItems] = useState([]);
  const [importing, setImporting] = useState(false);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      console.log('Selected file:', selectedFile.name, 'Type:', selectedFile.type, 'Size:', selectedFile.size);
      
      // Check file type - be more permissive with PDF detection
      const allowedTypes = [
        'application/pdf', 
        'text/plain', 
        'text/csv', 
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',  // Add legacy Excel
        'image/jpeg', 
        'image/png', 
        'image/jpg'
      ];
      
      // Also check by file extension as some browsers don't set MIME type correctly
      const fileName = selectedFile.name.toLowerCase();
      const isPDF = selectedFile.type === 'application/pdf' || fileName.endsWith('.pdf');
      const isText = selectedFile.type === 'text/plain' || fileName.endsWith('.txt');
      const isCSV = selectedFile.type === 'text/csv' || fileName.endsWith('.csv');
      const isExcel = selectedFile.type.includes('spreadsheet') || fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
      const isImage = selectedFile.type.startsWith('image/') || fileName.match(/\.(jpg|jpeg|png)$/);
      
      if (isPDF || isText || isCSV || isExcel || isImage || allowedTypes.includes(selectedFile.type)) {
        setFile(selectedFile);
        setParsedItems([]); // Reset parsed items
      } else {
        console.error('Unsupported file type:', selectedFile.type, 'for file:', selectedFile.name);
        toast.error(`Unsupported file type: ${selectedFile.type}. Please select a PDF, text file, CSV, Excel file, or image.`);
      }
    }
  };

  const parseReceipt = async () => {
    if (!file) {
      toast.error("Please select a file first");
      return;
    }

    setParsing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/ai/parse-receipt`, // New endpoint for receipt parsing
        formData,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          params: {
            batch_size: 100  // Request larger batch size for initial parsing
          }
        }
      );

      if (response.data.items && response.data.items.length > 0) {
        setParsedItems(response.data.items);
        
        let message = `Found ${response.data.count} materials in receipt`;
        if (response.data.chunks_processed > 1) {
          message += ` (processed in ${response.data.chunks_processed} chunks)`;
        }
        if (response.data.duplicates_removed > 0) {
          message += `, removed ${response.data.duplicates_removed} duplicates`;
        }
        if (response.data.has_more) {
          message += `. Large file - showing first ${response.data.count} items.`;
        }
        
        toast.success(message, { duration: 6000 });
        
        if (response.data.total_found > response.data.count) {
          toast.info(`Total found: ${response.data.total_found} materials. Processing in batches for better performance.`, {
            duration: 8000
          });
        }
      } else {
        toast.warning("No materials found in receipt");
        setParsedItems([]);
      }
    } catch (error) {
      let errorMessage = "Failed to parse receipt";
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(err => err.msg || err).join("; ");
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error("Failed to parse receipt: " + errorMessage);
    } finally {
      setParsing(false);
    }
  };

  const importItems = async () => {
    if (parsedItems.length === 0) {
      toast.error("No items to import");
      return;
    }

    setImporting(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      // Show progress for large imports
      const totalItems = parsedItems.length;
      
      if (totalItems > 50) {
        toast.info(`Importing ${totalItems} materials in batches. This may take a few minutes...`, {
          duration: 8000
        });
      }

      // Import items with enhanced batching and progress tracking
      for (let i = 0; i < parsedItems.length; i++) {
        const item = parsedItems[i];
        
        try {
          // Add progressive delays for large batches
          if (i > 0) {
            const delay = totalItems > 100 ? 800 : totalItems > 50 ? 600 : 400; // Adaptive delays
            await new Promise(resolve => setTimeout(resolve, delay));
          }
          
          // Show progress every 25 items
          if (i > 0 && i % 25 === 0) {
            const progress = Math.round((i / totalItems) * 100);
            toast.info(`Import progress: ${i}/${totalItems} materials (${progress}%)`, {
              duration: 3000
            });
          }

          await axios.post(`${API}/materials`, item, {
            headers: { Authorization: `Bearer ${token}` }
          });
          successCount++;
        } catch (error) {
          console.error(`Failed to import ${item.name}:`, error);
          errorCount++;
          
          // Stop import if too many errors occur
          if (errorCount > Math.min(10, totalItems * 0.2)) {
            toast.error(`Too many import errors (${errorCount}). Stopping import to prevent issues.`);
            break;
          }
        }
      }

      // Final results
      if (successCount > 0) {
        toast.success(`Successfully imported ${successCount} materials!`, {
          duration: 6000
        });
      }
      if (errorCount > 0) {
        toast.warning(`Failed to import ${errorCount} materials`);
      }
      
      onSuccess();
    } catch (error) {
      toast.error("Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <DialogContent className="bg-noir-paper border-zinc-800 max-w-4xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="text-white font-playfair">
          Import Receipt - Bulk Add Materials
        </DialogTitle>
      </DialogHeader>
      
      <div className="space-y-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-zinc-300">Upload Receipt File</Label>
            <Input
              type="file"
              onChange={handleFileChange}
              accept=".pdf,.txt,.csv,.xlsx,.jpg,.jpeg,.png"
              className="bg-noir-subtle border-zinc-700 text-white file:bg-amber file:text-black file:border-0 file:rounded file:px-3 file:py-1 file:mr-3"
            />
            <p className="text-xs text-zinc-500">
              Supported formats: PDF, Text, CSV, Excel, or Image files
            </p>
          </div>

          {file && (
            <div className="p-3 bg-zinc-800/50 border border-zinc-700 rounded">
              <p className="text-zinc-300 text-sm">
                Selected: <span className="text-white">{file.name}</span>
              </p>
              <p className="text-zinc-500 text-xs">
                Size: {(file.size / 1024).toFixed(1)} KB
              </p>
            </div>
          )}

          <Button
            onClick={parseReceipt}
            disabled={!file || parsing}
            className="bg-blue-600 hover:bg-blue-500 text-white"
          >
            {parsing ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Parsing Receipt...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Parse Receipt with AI
              </span>
            )}
          </Button>
        </div>

        {parsedItems.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-medium">Found Materials ({parsedItems.length})</h3>
              <Button
                onClick={importItems}
                disabled={importing}
                className="bg-green-600 hover:bg-green-500 text-white"
              >
                {importing ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Importing...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Import All to Inventory
                  </span>
                )}
              </Button>
            </div>

            <div className="max-h-96 overflow-y-auto border border-zinc-700 rounded">
              <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-noir-subtle border-b border-zinc-700 text-xs uppercase tracking-wider text-zinc-500">
                <div className="col-span-3">Material Name</div>
                <div className="col-span-2">Quantity</div>
                <div className="col-span-2">Price/Unit</div>
                <div className="col-span-2">Category</div>
                <div className="col-span-3">Supplier</div>
              </div>
              
              {parsedItems.map((item, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-zinc-800/50 hover:bg-zinc-800/30">
                  <div className="col-span-3">
                    <p className="text-white font-medium">{item.name}</p>
                    {item.cas_number && (
                      <p className="text-zinc-500 text-xs font-mono">{item.cas_number}</p>
                    )}
                  </div>
                  <div className="col-span-2">
                    <p className="text-amber font-mono">{item.quantity_grams || 0}g</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-green-400 font-mono">${item.price_per_gram || 0}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-blue-400">{item.category || "—"}</p>
                  </div>
                  <div className="col-span-3">
                    <p className="text-zinc-400">{item.supplier || "—"}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-3 bg-green-500/10 border border-green-500/30 rounded">
              <p className="text-green-400 text-sm">
                ✅ Review the materials above. Click "Import All" to add them to your inventory.
              </p>
            </div>
          </div>
        )}
      </div>

      <DialogFooter>
        <DialogClose asChild>
          <Button variant="ghost" className="text-zinc-400" onClick={onClose}>
            Cancel
          </Button>
        </DialogClose>
      </DialogFooter>
    </DialogContent>
  );
};

// Material Dialog Component - starts blank for new materials
const MaterialDialog = ({ material, onClose, onSuccess, token }) => {
  // Always start with empty form for new materials
  const getInitialFormData = () => {
    if (material) {
      return {
        name: material.name || "",
        cas_number: (material.cas_number && material.cas_number !== "null") ? material.cas_number : "",
        cas_numbers: material.cas_numbers || [],
        components: material.components || [],
        description: material.description || "",
        synonyms: material.synonyms || "",
        odor_profile: material.odor_profile || "",
        odor_impact: material.odor_impact || 100,
        note_type: material.note_type || material.duration || "", // Handle old field name
        scent_duration: material.scent_duration || "",
        ifra_limit: material.ifra_limit || "",
        solution_percentage: material.solution_percentage || 100,
        solution_type: material.solution_type || "Alcohol",
        category: material.category || "",
        supplier: material.supplier || "",
        price_per_gram: material.price_per_gram || "",
        quantity_grams: material.quantity_grams || "",
        reserve_grams: material.reserve_grams || "",
        initial_quantity_grams: material.initial_quantity_grams || "",
        initial_reserve_grams: material.initial_reserve_grams || ""
      };
    }
    // Blank form for new materials
    return {
      name: "",
      cas_number: "",
      cas_numbers: [],
      components: [],
      description: "",
      synonyms: "",
      odor_profile: "",
      odor_impact: 100,
      note_type: "",
      scent_duration: "",
      ifra_limit: "",
      solution_percentage: 100,
      solution_type: "Alcohol",
      category: "",
      supplier: "",
      price_per_gram: "",
      quantity_grams: "",
      reserve_grams: "",
      initial_quantity_grams: "",
      initial_reserve_grams: ""
    };
  };

  const [formData, setFormData] = useState(getInitialFormData);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState({ 
    description: false, 
    ifra: false, 
    standardize: false, 
    componentCas: null, 
    solution: false, 
    odorProfile: false,
    synonyms: false,
    scent_duration: false,
    odorImpactPW: false  // Add Perfumers World odor impact lookup
  });
  const [suggestions, setSuggestions] = useState(null);
  const [newCasNumber, setNewCasNumber] = useState("");
  const [newComponent, setNewComponent] = useState({ name: "", cas_number: "", ratio: 1, ifra_limit: "" });
  const [componentSearching, setComponentSearching] = useState(false);
  const nameTimeoutRef = useRef(null);

  const categories = ["Synthetic", "Natural", "Essential Oil", "Absolute", "Isolate", "Base", "Accord"];
  const solutionTypes = ["Alcohol", "IPM", "DPG", "TEC"];

  const handleChange = (field, value) => {
    setFormData(prev => {
      // Handle special numeric fields with validation
      if (field === "odor_impact") {
        // Allow empty string during typing, otherwise ensure valid number
        if (value === "" || value === null || value === undefined) {
          return { ...prev, [field]: "" };
        }
        const numericValue = parseInt(value);
        if (isNaN(numericValue) || numericValue < 1) {
          return { ...prev, [field]: 1 }; // Minimum value
        } else if (numericValue > 1000) {
          return { ...prev, [field]: 1000 }; // Maximum value
        } else {
          return { ...prev, [field]: numericValue };
        }
      } else if (field === "solution_percentage" || field === "price_per_gram" || field === "quantity_grams" || field === "reserve_grams") {
        // Handle other numeric fields - allow empty strings during typing
        if (value === "" || value === null || value === undefined) {
          return { ...prev, [field]: "" };
        }
        const numericValue = parseFloat(value);
        return { ...prev, [field]: isNaN(numericValue) ? "" : numericValue };
      } else {
        // Handle all other fields normally
        return { ...prev, [field]: value };
      }
    });
  };

  // Auto-lookup CAS when name changes (but don't change the name)
  const handleNameChange = (value) => {
    handleChange("name", value);
    setSuggestions(null);
    
    if (nameTimeoutRef.current) {
      clearTimeout(nameTimeoutRef.current);
    }
    
    if (value.length >= 3) {
      // Allow AI suggestions for both new materials and imported materials
      nameTimeoutRef.current = setTimeout(() => {
        lookupMaterialInfo(value);
      }, 800);
    }
  };

  const lookupMaterialInfo = async (name) => {
    setAiLoading(prev => ({ ...prev, standardize: true }));
    try {
      const response = await axios.post(
        `${API}/ai/standardize-material`,
        null,
        {
          params: { material_name: name },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      const data = response.data;
      setSuggestions(data);
      
      // Auto-fill CAS number if found (but NOT the name)
      if (data.cas_number && !formData.cas_number && formData.cas_numbers.length === 0) {
        handleChange("cas_number", data.cas_number);
      }
      
      if (data.category && !formData.category) {
        handleChange("category", data.category);
      }
    } catch (error) {
      console.error("Failed to lookup material info:", error);
    } finally {
      setAiLoading(prev => ({ ...prev, standardize: false }));
    }
  };

  const applyStandardName = () => {
    if (suggestions?.standardized_name) {
      handleChange("name", suggestions.standardized_name);
      toast.success("Applied standard name");
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error("Name is required");
      return;
    }

    setLoading(true);
    try {
      // Combine CAS numbers from primary and components
      const allCasNumbers = [];
      if (formData.cas_number) {
        allCasNumbers.push(formData.cas_number);
      }
      // Add CAS numbers from components
      formData.components.forEach(comp => {
        if (comp.cas_number && !allCasNumbers.includes(comp.cas_number)) {
          allCasNumbers.push(comp.cas_number);
        }
      });

      // Format components with proper types
      const formattedComponents = formData.components.map(comp => ({
        name: comp.name,
        cas_number: comp.cas_number || null,
        ratio: parseFloat(comp.ratio) || 0,
        ifra_limit: comp.ifra_limit ? parseFloat(comp.ifra_limit) : null
      }));

      const payload = {
        ...formData,
        cas_numbers: allCasNumbers,
        components: formattedComponents,
        ifra_limit: formData.ifra_limit || null, // Keep as string/text, don't convert to number
        solution_percentage: parseFloat(formData.solution_percentage) || 100,
        price_per_gram: formData.price_per_gram ? parseFloat(formData.price_per_gram) : null,
        quantity_grams: formData.quantity_grams ? parseFloat(formData.quantity_grams) : null,
        reserve_grams: formData.reserve_grams ? parseFloat(formData.reserve_grams) : null,
        initial_quantity_grams: formData.initial_quantity_grams ? parseFloat(formData.initial_quantity_grams) : (formData.quantity_grams ? parseFloat(formData.quantity_grams) : null),
        initial_reserve_grams: formData.initial_reserve_grams ? parseFloat(formData.initial_reserve_grams) : (formData.reserve_grams ? parseFloat(formData.reserve_grams) : null),
        odor_impact: parseInt(formData.odor_impact) || 100
      };

      if (material) {
        await axios.put(`${API}/materials/${material.material_id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Material updated");
      } else {
        await axios.post(`${API}/materials`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Material added");
      }
      onSuccess();
    } catch (error) {
      console.error("Save material error:", error);
      // Handle FastAPI validation errors properly
      let errorMessage = "Failed to save material";
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          // Multiple validation errors
          errorMessage = error.response.data.detail.map(err => err.msg || err).join("; ");
        } else if (typeof error.response.data.detail === 'string') {
          // Single error message
          errorMessage = error.response.data.detail;
        } else if (error.response.data.detail.msg) {
          // Single validation error object
          errorMessage = error.response.data.detail.msg;
        }
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const generateDescription = async () => {
    if (!formData.name) {
      toast.error("Enter a material name first");
      return;
    }
    setAiLoading(prev => ({ ...prev, description: true }));
    try {
      // Add delay to prevent rate limiting
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const response = await axios.post(
        `${API}/ai/generate-description`,
        null,
        {
          params: { material_name: formData.name, cas_number: formData.cas_number || null },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      handleChange("description", response.data.description);
      toast.success("Description generated");
    } catch (error) {
      console.error("AI Error:", error);
      if (error.response?.status === 429) {
        toast.error("Rate limit reached. Please wait a moment and try again.");
      } else {
        toast.error("Failed to generate description");
      }
    } finally {
      setAiLoading(prev => ({ ...prev, description: false }));
    }
  };

  const generateSynonyms = async () => {
    if (!formData.name) {
      toast.error("Enter a material name first");
      return;
    }
    setAiLoading(prev => ({ ...prev, synonyms: true }));
    try {
      // Add delay to prevent rate limiting
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const response = await axios.post(
        `${API}/ai/generate-synonyms`,
        null,
        {
          params: { material_name: formData.name, cas_number: formData.cas_number || null },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      handleChange("synonyms", response.data.synonyms);
      toast.success("Synonyms generated");
    } catch (error) {
      console.error("AI Error:", error);
      if (error.response?.status === 429) {
        toast.error("Rate limit reached. Please wait a moment and try again.");
      } else {
        toast.error("Failed to generate synonyms");
      }
    } finally {
      setAiLoading(prev => ({ ...prev, synonyms: false }));
    }
  };

  const lookupScentDuration = async () => {
    if (!formData.name) {
      toast.error("Enter a material name first");
      return;
    }
    setAiLoading(prev => ({ ...prev, scent_duration: true }));
    try {
      // Add delay to prevent rate limiting
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const response = await axios.post(
        `${API}/ai/lookup-scent-duration`,
        null,
        {
          params: { material_name: formData.name, note_type: formData.note_type || null },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      handleChange("scent_duration", response.data.scent_duration);
      toast.success("Duration information found");
    } catch (error) {
      console.error("AI Error:", error);
      if (error.response?.status === 429) {
        toast.error("Rate limit reached. Please wait a moment and try again.");
      } else {
        toast.error("Failed to lookup duration");
      }
    } finally {
      setAiLoading(prev => ({ ...prev, scent_duration: false }));
    }
  };

  const lookupOdorImpactPW = async () => {
    if (!formData.name) {
      toast.error("Enter a material name first");
      return;
    }
    setAiLoading(prev => ({ ...prev, odorImpactPW: true }));
    try {
      // Add delay to prevent rate limiting
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const response = await axios.post(
        `${API}/ai/lookup-odor-impact-pw`,
        null,
        {
          params: { material_name: formData.name, cas_number: formData.cas_number || null },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.odor_impact) {
        handleChange("odor_impact", response.data.odor_impact);
        const source = response.data.source || "Perfumers World";
        toast.success(`Odor impact found: ${response.data.odor_impact} (${source})`);
      } else {
        toast.info("No odor impact found for this material");
      }
    } catch (error) {
      console.error("Perfumers World Lookup Error:", error);
      if (error.response?.status === 429) {
        toast.error("Rate limit reached. Please wait a moment and try again.");
      } else {
        toast.error("Failed to lookup odor impact from Perfumers World");
      }
    } finally {
      setAiLoading(prev => ({ ...prev, odorImpactPW: false }));
    }
  };

  const generateOdorProfile = async () => {
    if (!formData.name) {
      toast.error("Enter a material name first");
      return;
    }
    setAiLoading(prev => ({ ...prev, odorProfile: true }));
    try {
      const response = await axios.post(
        `${API}/ai/generate-odor-profile`,
        null,
        {
          params: { material_name: formData.name, cas_number: formData.cas_number || null },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      handleChange("odor_profile", response.data.odor_profile);
      toast.success("Odor profile generated");
    } catch (error) {
      toast.error("Failed to generate odor profile");
    } finally {
      setAiLoading(prev => ({ ...prev, odorProfile: false }));
    }
  };

  const suggestSolution = async () => {
    if (!formData.name) {
      toast.error("Enter a material name first");
      return;
    }
    setAiLoading(prev => ({ ...prev, solution: true }));
    try {
      const response = await axios.post(
        `${API}/ai/suggest-solution`,
        null,
        {
          params: { material_name: formData.name },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (response.data.solution_type) {
        handleChange("solution_type", response.data.solution_type);
        toast.success(`Suggested: ${response.data.solution_type} - ${response.data.reason}`);
      }
    } catch (error) {
      toast.error("Failed to get solution suggestion");
    } finally {
      setAiLoading(prev => ({ ...prev, solution: false }));
    }
  };

  const lookupIFRA = async () => {
    if (!formData.name) {
      toast.error("Enter a material name first");
      return;
    }
    setAiLoading(prev => ({ ...prev, ifra: true }));
    try {
      const response = await axios.post(
        `${API}/ai/lookup-ifra`,
        null,
        {
          params: { material_name: formData.name, cas_number: formData.cas_number || null },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (response.data.ifra_limit) {
        handleChange("ifra_limit", response.data.ifra_limit);
        toast.success("IFRA limit found");
      } else {
        toast.info("No IFRA limit found for this material");
      }
    } catch (error) {
      toast.error("Failed to lookup IFRA limit");
    } finally {
      setAiLoading(prev => ({ ...prev, ifra: false }));
    }
  };

  // Component management functions for combination materials
  const lookupComponentCAS = async (index) => {
    const comp = formData.components[index];
    if (!comp.name) return;
    
    setAiLoading(prev => ({ ...prev, componentCas: index }));
    try {
      const response = await axios.post(
        `${API}/ai/standardize-material`,
        null,
        {
          params: { material_name: comp.name },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      const data = response.data;
      const updatedComponents = [...formData.components];
      if (data.cas_number) {
        updatedComponents[index].cas_number = data.cas_number;
      }
      handleChange("components", updatedComponents);
      toast.success(`CAS found for ${comp.name}`);
    } catch (error) {
      toast.error("Failed to lookup CAS number");
    } finally {
      setAiLoading(prev => ({ ...prev, componentCas: null }));
    }
  };

  const addComponent = () => {
    if (!newComponent.name.trim()) {
      toast.error("Enter a component name");
      return;
    }
    handleChange("components", [...formData.components, { ...newComponent, ratio: parseFloat(newComponent.ratio) || 1 }]);
    setNewComponent({ name: "", cas_number: "", ratio: 1, ifra_limit: "" });
  };

  const updateComponent = (index, field, value) => {
    const updatedComponents = [...formData.components];
    updatedComponents[index][field] = value;
    handleChange("components", updatedComponents);
  };

  const removeComponent = (index) => {
    handleChange("components", formData.components.filter((_, i) => i !== index));
  };

  return (
    <DialogContent className="bg-noir-paper border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="text-white font-playfair">
          {material ? "Edit Material" : "Add New Material"}
        </DialogTitle>
      </DialogHeader>

      <Tabs defaultValue="basic" className="w-full">
        <TabsList className="bg-noir-subtle border border-zinc-800">
          <TabsTrigger value="basic">Basic Info</TabsTrigger>
          <TabsTrigger value="components">Components</TabsTrigger>
          <TabsTrigger value="properties">Properties</TabsTrigger>
          <TabsTrigger value="inventory">Stock</TabsTrigger>
        </TabsList>

        <TabsContent value="basic" className="space-y-4 mt-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-zinc-300">Name * <span className="text-zinc-500 font-normal">(your choice)</span></Label>
              {aiLoading.standardize && (
                <span className="text-amber text-xs flex items-center gap-1">
                  <Sparkles className="w-3 h-3 animate-pulse" />
                  Looking up...
                </span>
              )}
            </div>
            <Input
              data-testid="material-name-input"
              value={formData.name}
              onChange={(e) => handleNameChange(e.target.value)}
              className="bg-noir-subtle border-zinc-700 text-white"
              placeholder="Enter your name for this material..."
            />
            {suggestions && suggestions.standardized_name && 
             suggestions.standardized_name.toLowerCase() !== formData.name.toLowerCase() && (
              <div className="p-3 bg-zinc-800/50 border border-zinc-700 space-y-2">
                <p className="text-xs text-zinc-400">
                  Standard name: <span className="text-zinc-300">{suggestions.standardized_name}</span>
                  <span className="text-zinc-500 ml-2">(optional)</span>
                </p>
                {suggestions.synonyms?.length > 0 && (
                  <p className="text-xs text-zinc-500">
                    Synonyms: {suggestions.synonyms.slice(0, 3).join(", ")}
                  </p>
                )}
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={applyStandardName}
                  className="text-zinc-400 hover:text-white text-xs h-6"
                >
                  Use standard name instead
                </Button>
              </div>
            )}
          </div>

          {/* Primary CAS Number */}
          <div className="space-y-2">
            <Label className="text-zinc-300">Primary CAS Number</Label>
            <Input
              value={formData.cas_number}
              onChange={(e) => handleChange("cas_number", e.target.value)}
              className="bg-noir-subtle border-zinc-700 text-white font-mono"
              placeholder="Auto-filled from AI lookup"
            />
            {formData.cas_number && (
              <p className="text-xs text-green-500">✓ CAS number found</p>
            )}
            <p className="text-xs text-zinc-500">For combination materials, use the "Components" tab to add multiple ingredients with CAS numbers</p>
          </div>

          {/* Synonyms */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-zinc-300">Alternative Names / Synonyms</Label>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={generateSynonyms}
                disabled={aiLoading.synonyms}
                className="text-amber hover:text-amber-light h-7 text-xs"
              >
                <Sparkles className="w-3 h-3 mr-1" />
                {aiLoading.synonyms ? "Generating..." : "AI Generate"}
              </Button>
            </div>
            <Input
              value={formData.synonyms}
              onChange={(e) => handleChange("synonyms", e.target.value)}
              className="bg-noir-subtle border-zinc-700 text-white"
              placeholder="Alternative names, comma-separated"
            />
            <p className="text-xs text-zinc-500">Searchable alternative names (e.g., "Linalyl acetate, Bergamyl acetate")</p>
          </div>

          {/* Note Type & Category Row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Note Type</Label>
              <Select value={formData.note_type || "none"} onValueChange={(v) => handleChange("note_type", v === "none" ? "" : v)}>
                <SelectTrigger className="bg-noir-subtle border-zinc-700 text-white">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select</SelectItem>
                  <SelectItem value="top">Top Note</SelectItem>
                  <SelectItem value="heart">Heart Note</SelectItem>
                  <SelectItem value="base">Base Note</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Category</Label>
              <Select value={formData.category || "none"} onValueChange={(v) => handleChange("category", v === "none" ? "" : v)}>
                <SelectTrigger className="bg-noir-subtle border-zinc-700 text-white">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select category</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-zinc-300">Description</Label>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={generateDescription}
                disabled={aiLoading.description}
                className="text-amber hover:text-amber-light h-7 text-xs"
              >
                <Sparkles className="w-3 h-3 mr-1" />
                {aiLoading.description ? "Generating..." : "AI Generate"}
              </Button>
            </div>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              className="w-full h-20 bg-noir-subtle border border-zinc-700 text-white p-3 resize-none focus:border-amber focus:outline-none text-sm"
              placeholder="Describe the material..."
            />
          </div>

          {/* Odor Profile - AI Keywords */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-zinc-300">Odor Profile <span className="text-zinc-500 font-normal">(keywords)</span></Label>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={generateOdorProfile}
                disabled={aiLoading.odorProfile}
                className="text-amber hover:text-amber-light h-7 text-xs"
              >
                <Sparkles className="w-3 h-3 mr-1" />
                {aiLoading.odorProfile ? "Generating..." : "AI Generate"}
              </Button>
            </div>
            <Input
              value={formData.odor_profile}
              onChange={(e) => handleChange("odor_profile", e.target.value)}
              className="bg-noir-subtle border-zinc-700 text-white"
              placeholder="e.g., floral, fresh, citrus, woody, sweet"
            />
            <p className="text-xs text-zinc-500">Comma-separated keywords for searching</p>
          </div>
        </TabsContent>

        {/* Components Tab for Combination Materials */}
        <TabsContent value="components" className="space-y-4 mt-4">
          <div className="p-3 bg-amber/10 border border-amber/30 mb-4">
            <p className="text-amber text-sm font-medium">Combination Materials</p>
            <p className="text-zinc-400 text-xs mt-1">
              For materials made of multiple ingredients (like accords or bases). Add each ingredient with its CAS number and ratio for IFRA compliance checking.
            </p>
          </div>

          {/* Existing Components List */}
          {formData.components.length > 0 && (
            <div className="space-y-2">
              <Label className="text-zinc-300">Added Components</Label>
              <div className="space-y-2">
                {formData.components.map((comp, idx) => (
                  <div key={idx} className="p-3 bg-noir-subtle border border-zinc-700 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-white font-medium">{comp.name}</span>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={() => removeComponent(idx)}
                        className="h-6 w-6 text-zinc-500 hover:text-red-400"
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label className="text-zinc-500 text-xs">CAS Number</Label>
                        <div className="flex gap-1">
                          <Input
                            value={comp.cas_number || ""}
                            onChange={(e) => updateComponent(idx, "cas_number", e.target.value)}
                            className="bg-noir border-zinc-800 text-white font-mono text-sm h-8"
                            placeholder="CAS #"
                          />
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            onClick={() => lookupComponentCAS(idx)}
                            disabled={aiLoading.componentCas === idx}
                            className="h-8 w-8 text-amber hover:text-amber-light shrink-0"
                          >
                            {aiLoading.componentCas === idx ? (
                              <span className="w-3 h-3 border border-amber border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Sparkles className="w-3 h-3" />
                            )}
                          </Button>
                        </div>
                      </div>
                      <div>
                        <Label className="text-zinc-500 text-xs">Ratio (%)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={comp.ratio}
                          onChange={(e) => updateComponent(idx, "ratio", parseFloat(e.target.value) || 0)}
                          className="bg-noir border-zinc-800 text-white font-mono text-sm h-8"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-500 text-xs">IFRA Limit (%)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={comp.ifra_limit || ""}
                          onChange={(e) => updateComponent(idx, "ifra_limit", e.target.value ? parseFloat(e.target.value) : null)}
                          className="bg-noir border-zinc-800 text-white font-mono text-sm h-8"
                          placeholder="Optional"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add New Component */}
          <div className="space-y-3 p-4 bg-zinc-800/30 border border-zinc-700">
            <Label className="text-zinc-300 flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Add New Component
            </Label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-500 text-xs">Ingredient Name *</Label>
                <Input
                  data-testid="component-name-input"
                  value={newComponent.name}
                  onChange={(e) => setNewComponent(prev => ({ ...prev, name: e.target.value }))}
                  className="bg-noir-subtle border-zinc-700 text-white"
                  placeholder="e.g., Linalool"
                />
              </div>
              <div>
                <Label className="text-zinc-500 text-xs">CAS Number</Label>
                <Input
                  value={newComponent.cas_number}
                  onChange={(e) => setNewComponent(prev => ({ ...prev, cas_number: e.target.value }))}
                  className="bg-noir-subtle border-zinc-700 text-white font-mono"
                  placeholder="e.g., 78-70-6"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-500 text-xs">Ratio in Mixture (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={newComponent.ratio}
                  onChange={(e) => setNewComponent(prev => ({ ...prev, ratio: e.target.value }))}
                  className="bg-noir-subtle border-zinc-700 text-white font-mono"
                />
              </div>
              <div>
                <Label className="text-zinc-500 text-xs">IFRA Limit (%) - Optional</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={newComponent.ifra_limit}
                  onChange={(e) => setNewComponent(prev => ({ ...prev, ifra_limit: e.target.value }))}
                  className="bg-noir-subtle border-zinc-700 text-white font-mono"
                  placeholder="e.g., 5.4"
                />
              </div>
            </div>
            <Button
              type="button"
              onClick={addComponent}
              className="w-full bg-amber/20 hover:bg-amber/30 text-amber border border-amber/30"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Component
            </Button>
          </div>

          {formData.components.length > 0 && (
            <div className="p-3 bg-zinc-800/50 border border-zinc-700">
              <p className="text-xs text-zinc-400">
                <strong className="text-zinc-300">Total components:</strong> {formData.components.length}
                {" | "}
                <strong className="text-zinc-300">Total ratio:</strong> {formData.components.reduce((sum, c) => sum + (parseFloat(c.ratio) || 0), 0).toFixed(1)}%
              </p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="properties" className="space-y-4 mt-4">
          {/* Solution Settings */}
          <div className="p-4 bg-zinc-800/30 border border-zinc-700 space-y-4">
            <p className="text-zinc-300 font-medium text-sm">Dilution Settings</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Solution %</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={formData.solution_percentage}
                  onChange={(e) => handleChange("solution_percentage", e.target.value)}
                  className="bg-noir-subtle border-zinc-700 text-white font-mono"
                />
                <p className="text-xs text-zinc-500">100% = Pure / Undiluted</p>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-zinc-300">Solution Type</Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={suggestSolution}
                    disabled={aiLoading.solution}
                    className="text-amber hover:text-amber-light h-6 text-xs"
                  >
                    <Sparkles className="w-3 h-3 mr-1" />
                    {aiLoading.solution ? "..." : "AI"}
                  </Button>
                </div>
                <Select value={formData.solution_type || "Alcohol"} onValueChange={(v) => handleChange("solution_type", v)}>
                  <SelectTrigger className="bg-noir-subtle border-zinc-700 text-white">
                    <SelectValue placeholder="Select solution" />
                  </SelectTrigger>
                  <SelectContent>
                    {solutionTypes.map(sol => (
                      <SelectItem key={sol} value={sol}>{sol}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-zinc-300">Odor Impact (1-1000)</Label>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={lookupOdorImpactPW}
                disabled={aiLoading.odorImpactPW}
                className="text-blue-400 hover:text-blue-300 h-7 text-xs"
              >
                <Sparkles className="w-3 h-3 mr-1" />
                {aiLoading.odorImpactPW ? "Looking up..." : "Perfumers World"}
              </Button>
            </div>
            <Input
              type="number"
              min="1"
              max="1000"
              value={formData.odor_impact}
              onChange={(e) => handleChange("odor_impact", e.target.value)}
              className="bg-noir-subtle border-zinc-700 text-white font-mono"
            />
            <p className="text-xs text-zinc-500">1 = Very weak • 100 = Medium • 1000 = Extremely strong</p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-zinc-300">Scent Duration</Label>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={lookupScentDuration}
                disabled={aiLoading.scent_duration}
                className="text-amber hover:text-amber-light h-7 text-xs"
              >
                <Sparkles className="w-3 h-3 mr-1" />
                {aiLoading.scent_duration ? "Looking up..." : "AI Lookup"}
              </Button>
            </div>
            <Input
              value={formData.scent_duration}
              onChange={(e) => handleChange("scent_duration", e.target.value)}
              className="bg-noir-subtle border-zinc-700 text-white"
              placeholder="e.g., 4-6 hours, 12+ hours"
            />
            <p className="text-xs text-zinc-500">How long the scent typically lasts (e.g., "2-4 hours")</p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-zinc-300">IFRA Limit (%)</Label>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={lookupIFRA}
                disabled={aiLoading.ifra}
                className="text-amber hover:text-amber-light h-7 text-xs"
              >
                <Sparkles className="w-3 h-3 mr-1" />
                {aiLoading.ifra ? "Looking up..." : "AI Lookup"}
              </Button>
            </div>
            <Input
              type="text"  
              value={formData.ifra_limit}
              onChange={(e) => handleChange("ifra_limit", e.target.value)}
              className="bg-noir-subtle border-zinc-700 text-white font-mono"
              placeholder="e.g., 5.4%, No restriction"
            />
          </div>
        </TabsContent>

        <TabsContent value="inventory" className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Supplier</Label>
              <Input
                value={formData.supplier}
                onChange={(e) => handleChange("supplier", e.target.value)}
                className="bg-noir-subtle border-zinc-700 text-white"
                placeholder="e.g., Firmenich"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Price per Gram ($)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={formData.price_per_gram}
                onChange={(e) => handleChange("price_per_gram", e.target.value)}
                className="bg-noir-subtle border-zinc-700 text-white font-mono"
              />
            </div>
          </div>

          <div className="p-4 bg-blue-500/10 border border-blue-500/30 space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <Label className="text-blue-400 font-medium">Stock (Diluted at {formData.solution_percentage || 100}%)</Label>
            </div>
            <Input
              type="number"
              min="0"
              step="0.1"
              value={formData.quantity_grams}
              onChange={(e) => handleChange("quantity_grams", e.target.value)}
              className="bg-noir-subtle border-blue-500/50 text-blue-400 font-mono"
              placeholder="Grams in stock"
            />
          </div>

          <div className="p-4 bg-green-500/10 border border-green-500/30 space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <Label className="text-green-400 font-medium">Reserve (100% Undiluted)</Label>
            </div>
            <Input
              type="number"
              min="0"
              step="0.1"
              value={formData.reserve_grams}
              onChange={(e) => handleChange("reserve_grams", e.target.value)}
              className="bg-noir-subtle border-green-500/50 text-green-400 font-mono"
              placeholder="Grams in reserve"
            />
          </div>
        </TabsContent>
      </Tabs>

      <DialogFooter className="mt-6">
        <DialogClose asChild>
          <Button variant="ghost" className="text-zinc-400">Cancel</Button>
        </DialogClose>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          className="bg-amber hover:bg-amber-light text-black"
        >
          {loading ? "Saving..." : (material ? "Update" : "Add Material")}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};


// Import CSV Dialog Component
const ImportCSVDialog = ({ onClose, onSuccess, token }) => {
  const [file, setFile] = useState(null);
  const [parsedItems, setParsedItems] = useState([]);
  const [importing, setImporting] = useState(false);
  const [parseError, setParseError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileName = selectedFile.name.toLowerCase();
      if (fileName.endsWith('.csv') || fileName.endsWith('.txt')) {
        setFile(selectedFile);
        setParsedItems([]);
        setParseError(null);
        parseCSV(selectedFile);
      } else {
        toast.error("Please select a CSV file");
      }
    }
  };

  const triggerFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const parseCSV = async (csvFile) => {
    try {
      const text = await csvFile.text();
      
      // Use a proper CSV parser that handles multi-line quoted values
      const rows = parseCSVText(text);
      
      if (rows.length < 2) {
        setParseError("CSV file is empty or has no data rows");
        return;
      }

      // Parse header row
      const headers = rows[0].map(h => h.toLowerCase().trim());
      
      // Map headers to material fields
      const headerMap = {
        'name': 'name',
        'category': 'category',
        'quantity (g)': 'quantity_grams',
        'quantity_grams': 'quantity_grams',
        'quantity': 'quantity_grams',
        'price per gram': 'price_per_gram',
        'price_per_gram': 'price_per_gram',
        'price': 'price_per_gram',
        'solution %': 'solution_percentage',
        'solution_percentage': 'solution_percentage',
        'solution': 'solution_percentage',
        'solution type': 'solution_type',
        'solution_type': 'solution_type',
        'supplier': 'supplier',
        'cas number': 'cas_number',
        'cas_number': 'cas_number',
        'cas': 'cas_number',
        'note type': 'note_type',
        'note_type': 'note_type',
        'note': 'note_type',
        'scent duration': 'scent_duration',
        'scent_duration': 'scent_duration',
        'duration': 'scent_duration',
        'odor impact': 'odor_impact',
        'odor_impact': 'odor_impact',
        'impact': 'odor_impact',
        'ifra limit': 'ifra_limit',
        'ifra_limit': 'ifra_limit',
        'ifra': 'ifra_limit',
        'odor profile': 'odor_profile',
        'odor_profile': 'odor_profile',
        'profile': 'odor_profile',
        'description': 'description',
        'synonyms': 'synonyms'
      };

      const items = [];
      for (let i = 1; i < rows.length; i++) {
        const values = rows[i];
        if (!values || values.length === 0 || (values.length === 1 && !values[0])) continue;

        const item = {};
        headers.forEach((header, index) => {
          const field = headerMap[header];
          if (field && values[index] !== undefined) {
            let value = String(values[index]).trim();
            
            // Skip fields with very long AI-generated content (likely broken IFRA limits)
            if (value.length > 500 && field === 'ifra_limit') {
              // Extract just a simple limit if possible
              const percentMatch = value.match(/(\d+\.?\d*)\s*%/);
              value = percentMatch ? percentMatch[1] : '';
            }
            
            // Convert numeric fields
            if (['quantity_grams', 'price_per_gram', 'solution_percentage', 'odor_impact'].includes(field)) {
              const num = parseFloat(value);
              value = isNaN(num) ? (field === 'solution_percentage' ? 100 : 0) : num;
            }
            
            // Truncate very long text fields
            if (typeof value === 'string' && value.length > 1000) {
              value = value.substring(0, 1000);
            }
            
            item[field] = value;
          }
        });

        // Only add items with a valid name (not random text from broken CSV rows)
        if (item.name && item.name.trim() && item.name.length < 200) {
          // Validate it looks like a material name (not markdown or long text)
          const name = item.name.trim();
          if (!name.startsWith('#') && !name.startsWith('**') && !name.startsWith('-')) {
            // Set defaults
            item.category = item.category || 'Synthetic';
            item.quantity_grams = item.quantity_grams || 0;
            item.price_per_gram = item.price_per_gram || 0;
            item.solution_percentage = item.solution_percentage || 100;
            items.push(item);
          }
        }
      }

      if (items.length === 0) {
        setParseError("No valid materials found in CSV. Make sure the first row has headers including 'name'.");
        return;
      }

      setParsedItems(items);
      toast.success(`Parsed ${items.length} materials from CSV`);
    } catch (error) {
      console.error("CSV parse error:", error);
      setParseError("Failed to parse CSV file: " + error.message);
    }
  };

  // Proper CSV parser that handles multi-line quoted values
  const parseCSVText = (text) => {
    const rows = [];
    let currentRow = [];
    let currentValue = '';
    let inQuotes = false;
    
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const nextChar = text[i + 1];
      
      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          // Escaped quote
          currentValue += '"';
          i++;
        } else {
          // Toggle quote mode
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        // End of field
        currentRow.push(currentValue);
        currentValue = '';
      } else if ((char === '\n' || (char === '\r' && nextChar === '\n')) && !inQuotes) {
        // End of row
        currentRow.push(currentValue);
        if (currentRow.some(v => v.trim())) { // Only add non-empty rows
          rows.push(currentRow);
        }
        currentRow = [];
        currentValue = '';
        if (char === '\r') i++; // Skip \n after \r
      } else if (char === '\r' && !inQuotes) {
        // End of row (just \r)
        currentRow.push(currentValue);
        if (currentRow.some(v => v.trim())) {
          rows.push(currentRow);
        }
        currentRow = [];
        currentValue = '';
      } else {
        currentValue += char;
      }
    }
    
    // Don't forget the last row
    if (currentValue || currentRow.length > 0) {
      currentRow.push(currentValue);
      if (currentRow.some(v => v.trim())) {
        rows.push(currentRow);
      }
    }
    
    return rows;
  };

  // Legacy helper function (kept for compatibility)
  const parseCSVRow = (row) => {
    return parseCSVText(row)[0] || [];
  };

  const importItems = async () => {
    if (parsedItems.length === 0) return;
    
    setImporting(true);
    try {
      let successCount = 0;
      let errorCount = 0;
      
      // Import in batches of 10
      const batchSize = 10;
      for (let i = 0; i < parsedItems.length; i += batchSize) {
        const batch = parsedItems.slice(i, i + batchSize);
        
        for (const item of batch) {
          try {
            await axios.post(`${API}/materials`, item, {
              headers: { Authorization: `Bearer ${token}` }
            });
            successCount++;
          } catch (error) {
            console.error(`Failed to import ${item.name}:`, error);
            errorCount++;
          }
        }
        
        // Show progress
        const progress = Math.round(((i + batch.length) / parsedItems.length) * 100);
        toast.info(`Import progress: ${progress}%`, { duration: 1000 });
      }
      
      if (errorCount > 0) {
        toast.warning(`Imported ${successCount} materials, ${errorCount} failed`);
      } else {
        toast.success(`Successfully imported ${successCount} materials`);
      }
      
      onSuccess();
    } catch (error) {
      console.error("Import error:", error);
      toast.error("Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <DialogContent className="bg-noir-card border-zinc-800 max-w-2xl max-h-[80vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="text-white font-playfair text-xl">Import CSV</DialogTitle>
      </DialogHeader>

      <div className="space-y-4 py-4">
        <div className="space-y-2">
          <Label className="text-zinc-300">Upload CSV File</Label>
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileChange}
            accept=".csv,.txt,text/csv,text/plain,application/csv"
            className="hidden"
          />
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={triggerFileSelect}
              className="bg-teal-600 hover:bg-teal-500 text-white"
            >
              <Upload className="w-4 h-4 mr-2" />
              Choose CSV File
            </Button>
            {file && (
              <span className="text-zinc-300 text-sm flex items-center">
                {file.name}
              </span>
            )}
          </div>
          <p className="text-xs text-zinc-500">
            CSV should have headers: Name, Category, Quantity (g), Price per Gram, Solution %, etc.
          </p>
          <p className="text-xs text-zinc-400">
            Tip: Export your inventory first to see the expected format.
          </p>
        </div>

        {file && (
          <div className="p-3 bg-zinc-800/50 border border-zinc-700 rounded">
            <p className="text-zinc-300 text-sm">
              Selected: <span className="text-white">{file.name}</span>
            </p>
            <p className="text-zinc-500 text-xs">
              Size: {(file.size / 1024).toFixed(1)} KB
            </p>
          </div>
        )}

        {parseError && (
          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded">
            <p className="text-red-400 text-sm">{parseError}</p>
          </div>
        )}

        {parsedItems.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-medium">Found Materials ({parsedItems.length})</h3>
              <Button
                onClick={importItems}
                disabled={importing}
                className="bg-teal-600 hover:bg-teal-500 text-white"
              >
                {importing ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Importing...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Upload className="w-4 h-4" />
                    Import All
                  </span>
                )}
              </Button>
            </div>

            <div className="max-h-60 overflow-y-auto space-y-2 border border-zinc-700 rounded p-3">
              {parsedItems.slice(0, 50).map((item, index) => (
                <div key={index} className="grid grid-cols-12 gap-2 text-sm py-2 border-b border-zinc-800 last:border-0">
                  <div className="col-span-4">
                    <p className="text-white font-medium truncate">{item.name}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-amber">{item.quantity_grams}g</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-blue-400">{item.category || "—"}</p>
                  </div>
                  <div className="col-span-4">
                    <p className="text-zinc-400 truncate">{item.scent_duration || item.supplier || "—"}</p>
                  </div>
                </div>
              ))}
              {parsedItems.length > 50 && (
                <p className="text-zinc-500 text-sm text-center py-2">
                  ...and {parsedItems.length - 50} more materials
                </p>
              )}
            </div>

            <div className="p-3 bg-teal-500/10 border border-teal-500/30 rounded">
              <p className="text-teal-400 text-sm">
                ✅ Review the materials above. Click "Import All" to add them to your inventory.
              </p>
            </div>
          </div>
        )}
      </div>

      <DialogFooter>
        <DialogClose asChild>
          <Button variant="ghost" className="text-zinc-400" onClick={onClose}>
            Cancel
          </Button>
        </DialogClose>
      </DialogFooter>
    </DialogContent>
  );
};
